/* ============================================================
   DIVA CHIC — global storefront behaviours
   Ported 1:1 from the approved static designs (announce rotator,
   sticky header, drawer, search modal, toast, wishlist, cart,
   reveal animations, carousels, PDP, collection filtering).
   ============================================================ */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ============================================================
     PRICES — hidden for signed-out shoppers (login to view price)
     ============================================================ */
  const showPrices = window.DC_SHOW_PRICES !== false;

  /* ============================================================
     MONEY — format cents with the shop money format
     ============================================================ */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const format = window.DC_MONEY_FORMAT || '£{{amount}}';
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    const noDecimals = (cents / 100).toFixed(0);
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  /* ============================================================
     TOAST
     ============================================================ */
  const toastEl = () => $('#toast');
  const toastMsg = () => $('#toastMsg');
  let toastTimer;
  function showToast(msg) {
    const t = toastEl(), m = toastMsg();
    if (!t || !m) return;
    m.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('is-visible'), 2600);
  }

  /* ============================================================
     BAG — drawer + page, live via the Section Rendering API
     ============================================================ */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.dataset.empty = count === 0 ? 'true' : 'false';
      if (count > 0) {
        el.classList.add('bump');
        setTimeout(() => el.classList.remove('bump'), 260);
      }
    });
  }

  function cartSectionIds() {
    const ids = [];
    ['#cartDrawer', '[data-cart-page]'].forEach(sel => {
      const el = $(sel);
      const wrap = el && el.closest('[id^="shopify-section-"]');
      if (wrap) ids.push(wrap.id.replace('shopify-section-', ''));
    });
    return ids;
  }

  function applyCartSections(sections) {
    if (!sections) return;
    cartSectionIds().forEach(id => {
      let html = sections[id];
      const wrap = document.getElementById('shopify-section-' + id);
      if (html && wrap) {
        /* the API returns the section wrapped in its shopify-section div — unwrap */
        const tpl = document.createElement('div');
        tpl.innerHTML = html.trim();
        const inner = tpl.firstElementChild;
        if (inner && inner.id === 'shopify-section-' + id) html = inner.innerHTML;
        wrap.innerHTML = html;
        initReveal(wrap);
      }
    });
  }

  function openCartDrawer() {
    const cd = $('#cartDrawer');
    if (!cd) { window.location.href = '/cart'; return; }
    cd.classList.add('is-open');
    cd.setAttribute('aria-hidden', 'false');
    const overlay = $('#overlay');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  async function changeLine(line, quantity) {
    const body = { line, quantity };
    const sections = cartSectionIds().join(',');
    if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      updateCartCount(data.item_count);
      applyCartSections(data.sections);
    } catch {}
  }

  async function addToCart(id, quantity, successMsg) {
    try {
      const body = { items: [{ id, quantity }] };
      const sections = cartSectionIds().join(',');
      if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error((await res.json()).description || 'Unable to add to bag');
      const data = await res.json();
      updateCartCount(data.item_count);
      applyCartSections(data.sections);
      openCartDrawer();
      return true;
    } catch (err) {
      showToast(typeof err === 'string' ? err : (err.message || 'Unable to add to bag'));
      return false;
    }
  }

  /* drawer open/close + line quantity steppers (delegated — survives re-renders) */
  document.addEventListener('click', e => {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); openCartDrawer(); return; }
    if (e.target.closest('[data-cart-close]')) { closeOverlays(); return; }
    const minus = e.target.closest('[data-line-minus]');
    const plus = e.target.closest('[data-line-plus]');
    const removeBtn = e.target.closest('[data-line-remove]');
    if (!(minus || plus || removeBtn)) return;
    const row = (minus || plus || removeBtn).closest('[data-line]');
    if (!row) return;
    const input = $('[data-line-input]', row);
    let qty = input ? (parseInt(input.value, 10) || 0) : 1;
    if (minus) qty = Math.max(0, qty - 1);
    if (plus) qty += 1;
    if (removeBtn) qty = 0;
    changeLine(parseInt(row.dataset.line, 10), qty);
  });

  /* quick-add on product cards */
  document.addEventListener('click', e => {
    const add = e.target.closest('.card__add');
    if (!add) return;
    const id = add.dataset.variantId;
    if (!id) { showToast('Sorry — this item is currently unavailable'); return; }
    addToCart(id, 1, `Added to your bag — ${add.dataset.name || 'your item'}`);
  });

  /* ============================================================
     WISHLIST — persisted per product handle
     ============================================================ */
  const WISH_KEY = 'dc_wishlist';
  function getWish() {
    try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; }
    catch { return []; }
  }
  function setWish(list) {
    try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch {}
    updateWishUI(list);
  }
  function updateWishUI(list) {
    $$('[data-wish-handle]').forEach(btn => {
      btn.setAttribute('aria-pressed', String(list.includes(btn.dataset.wishHandle)));
    });
    $$('[data-wish-count]').forEach(el => {
      el.textContent = list.length;
      el.dataset.empty = list.length === 0 ? 'true' : 'false';
    });
  }

  document.addEventListener('click', e => {
    const wishBtn = e.target.closest('[data-wish-handle]');
    if (!wishBtn) return;
    const handle = wishBtn.dataset.wishHandle;
    const list = getWish();
    const on = list.includes(handle);
    setWish(on ? list.filter(h => h !== handle) : [...list, handle]);
    showToast(on ? 'Removed from wishlist' : 'Saved to your wishlist');
  });

  /* ============================================================
     ANNOUNCEMENT ROTATOR
     ============================================================ */
  function initAnnounce() {
    $$('.announce').forEach(bar => {
      if (bar.dataset.rotatorBound) return;
      bar.dataset.rotatorBound = '1';
      const msgs = $$('.announce__msg', bar);
      let idx = 0;
      if (msgs.length > 1) {
        setInterval(() => {
          msgs[idx].classList.remove('is-active');
          idx = (idx + 1) % msgs.length;
          msgs[idx].classList.add('is-active');
        }, 4200);
      }
    });
  }

  /* ============================================================
     STICKY HEADER · TO TOP
     ============================================================ */
  function initScrollFX() {
    if (window.__dcScrollBound) return;
    window.__dcScrollBound = true;
    const header = $('#header');
    const totop = $('#totop');
    window.addEventListener('scroll', () => {
      if (header) header.classList.toggle('is-stuck', window.scrollY > 60);
      if (totop) totop.classList.toggle('is-visible', window.scrollY > 700);
    }, { passive: true });
    if (totop) totop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ============================================================
     DRAWER (mobile nav) · SEARCH MODAL · OVERLAY
     ============================================================ */
  function closeOverlays() {
    ['#drawer', '.search-modal', '.filters', '#cartDrawer'].forEach(sel => {
      const el = $(sel);
      if (el) el.classList.remove('is-open');
    });
    const cd = $('#cartDrawer');
    if (cd) cd.setAttribute('aria-hidden', 'true');
    hideSuggest();
    const overlay = $('#overlay');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }
  function openLayer(sel) {
    const el = $(sel);
    if (!el) return;
    el.classList.add('is-open');
    const overlay = $('#overlay');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  document.addEventListener('click', e => {
    if (e.target.closest('[data-drawer-open]')) {
      const drawer = $('#drawer');
      openLayer('#drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        const burger = e.target.closest('[data-drawer-open]');
        burger.setAttribute('aria-expanded', 'true');
      }
    }
    if (e.target.closest('[data-drawer-close]')) {
      const drawer = $('#drawer');
      if (drawer) drawer.setAttribute('aria-hidden', 'true');
      const burger = $('[data-drawer-open]');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      closeOverlays();
    }
    if (e.target.closest('[data-search-open]')) {
      openLayer('.search-modal');
      setTimeout(() => { const i = $('[data-search-input]'); if (i) i.focus(); }, 120);
    }
    if (e.target.closest('[data-search-close]')) closeOverlays();
    if (e.target.closest('#overlay')) closeOverlays();
    const drawerLink = e.target.closest('#drawer a');
    if (drawerLink) {
      const drawer = $('#drawer');
      if (drawer) drawer.setAttribute('aria-hidden', 'true');
      const burger = $('[data-drawer-open]');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      closeOverlays();
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeOverlays();
  });

  /* ============================================================
     RAIL ARROWS (desktop carousels — gallery · reviews)
     ============================================================ */
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-scroll-rail]');
    if (!btn) return;
    const rail = $(btn.dataset.scrollRail);
    if (!rail) return;
    const first = rail.firstElementChild;
    const step = (first ? first.offsetWidth : 250) + 10;
    const dir = btn.dataset.scrollDir === 'prev' ? -1 : 1;
    rail.scrollBy({ left: dir * step, behavior: 'smooth' });
  });

  /* ============================================================
     DOT NAVIGATION (mobile: USP · product rail · reviews)
     ============================================================ */
  function bindDots(railEl, dotsEl, itemSel) {
    if (!railEl || !dotsEl) return;
    dotsEl.innerHTML = '';
    const items = $$(itemSel, railEl);
    if (!items.length) { dotsEl.style.display = 'none'; return; }
    dotsEl.style.display = '';
    const dots = items.map((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'Go to item ' + (i + 1));
      if (i === 0) b.classList.add('is-active');
      b.addEventListener('click', () => {
        const rl = railEl.getBoundingClientRect();
        const it = items[i].getBoundingClientRect();
        railEl.scrollTo({ left: railEl.scrollLeft + (it.left - rl.left), behavior: 'smooth' });
      });
      dotsEl.appendChild(b);
      return b;
    });
    const sync = () => {
      const rl = railEl.getBoundingClientRect();
      const mid = rl.left + rl.width / 2;
      let best = 0, dist = Infinity;
      items.forEach((it, i) => {
        const c = it.getBoundingClientRect().left + it.offsetWidth / 2;
        const d = Math.abs(c - mid);
        if (d < dist) { dist = d; best = i; }
      });
      dots.forEach((d, i) => d.classList.toggle('is-active', i === best));
    };
    /* one scroll listener per rail, even when cards are re-rendered */
    if (!railEl.dataset.dotsBound) {
      railEl.dataset.dotsBound = '1';
      let raf = null;
      railEl.addEventListener('scroll', () => {
        if (!raf) raf = requestAnimationFrame(() => { raf = null; railEl.__dotsSync && railEl.__dotsSync(); });
      }, { passive: true });
    }
    railEl.__dotsSync = sync;
    sync();
  }

  /* ============================================================
     IMAGE FALLBACK — elegant placeholder if a photo 404s
     ============================================================ */
  document.addEventListener('error', e => {
    const el = e.target;
    if (!el.tagName || el.tagName !== 'IMG') return;
    const box = el.parentElement;
    if (!box || box.classList.contains('media-fb')) return;
    const label = el.getAttribute('alt') || (el.closest('[data-fb]') && el.closest('[data-fb]').dataset.fb) || 'Diva Chic';
    box.classList.add('media-fb');
    box.dataset.fb = label;
    el.remove();
  }, true);

  /* ============================================================
     SCROLL REVEAL
     ============================================================ */
  const revealIO = new IntersectionObserver(entries => {
    entries.forEach(en => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px' });

  function initReveal(root = document) {
    $$('.reveal:not(.is-in)', root).forEach(el => revealIO.observe(el));
  }

  /* ============================================================
     FOOTER ACCORDIONS — collapsed on mobile, pinned open on desktop
     ============================================================ */
  function initFooterAccs() {
    const accMQ = window.matchMedia('(max-width: 900px)');
    const footerAccs = $$('.footer__acc');
    const syncFooterAccs = () => footerAccs.forEach(d => { d.open = !accMQ.matches; });
    if (!window.__dcAccBound) {
      window.__dcAccBound = true;
      accMQ.addEventListener('change', syncFooterAccs);
    }
    syncFooterAccs();
  }

  /* ============================================================
     HERO CAROUSEL
     ============================================================ */
  function initHero() {
    $$('[data-hero]').forEach(hero => {
      if (hero.dataset.heroBound) return;
      hero.dataset.heroBound = '1';
      const slides = $$('.hero__slide', hero);
      const dotsBox = $('[data-hero-dots]', hero);
      if (!slides.length || !dotsBox) return;
      let slideIdx = 0, heroTimer;

      slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        if (i === 0) b.classList.add('is-active');
        b.addEventListener('click', () => { goTo(i); restartHero(); });
        dotsBox.appendChild(b);
      });
      const dots = [...dotsBox.children];

      function goTo(i) {
        slides[slideIdx].classList.remove('is-active');
        if (dots[slideIdx]) dots[slideIdx].classList.remove('is-active');
        slideIdx = (i + slides.length) % slides.length;
        slides[slideIdx].classList.add('is-active');
        if (dots[slideIdx]) dots[slideIdx].classList.add('is-active');
      }
      function restartHero() {
        clearInterval(heroTimer);
        heroTimer = setInterval(() => goTo(slideIdx + 1), 5000);
      }
      restartHero();

      /* re-arm autoplay when the tab regains focus */
      document.addEventListener('visibilitychange', () => { if (!document.hidden) restartHero(); });

      /* touch swipe */
      let touchX = null;
      hero.addEventListener('touchstart', e => { touchX = e.touches[0].clientX; }, { passive: true });
      hero.addEventListener('touchend', e => {
        if (touchX === null) return;
        const dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 50) { goTo(slideIdx + (dx < 0 ? 1 : -1)); restartHero(); }
        touchX = null;
      }, { passive: true });
    });
  }

  /* ============================================================
     PRODUCT TABS (tabbed rail) — panels toggled, dots rebuilt
     ============================================================ */
  function initTabs() {
    $$('[data-tabs]').forEach(tabsRoot => {
      const dotsEl = $('[data-rail-dots]', tabsRoot);
      const activate = key => {
        $$('[data-tab-btn]', tabsRoot).forEach(b => b.setAttribute('aria-selected', String(b.dataset.tabBtn === key)));
        $$('[data-tab-panel]', tabsRoot).forEach(p => { p.hidden = p.dataset.tabPanel !== key; });
        const panel = $(`[data-tab-panel="${key}"]`, tabsRoot);
        if (panel && dotsEl) bindDots($('.rail', panel), dotsEl, '.card');
      };
      if (!tabsRoot.dataset.tabsBound) {
        tabsRoot.dataset.tabsBound = '1';
        tabsRoot.addEventListener('click', e => {
          const btn = e.target.closest('[data-tab-btn]');
          if (btn) activate(btn.dataset.tabBtn);
        });
      }
      const active = $('[data-tab-btn][aria-selected="true"]', tabsRoot);
      if (active) activate(active.dataset.tabBtn);
    });
  }

  /* ============================================================
     SALE COUNTDOWN
     ============================================================ */
  function initCountdowns() {
    $$('[data-countdown-end]').forEach(box => {
      if (box.dataset.cdBound) return;
      box.dataset.cdBound = '1';
      const end = new Date(box.dataset.countdownEnd).getTime();
      const pad = n => String(Math.max(0, n)).padStart(2, '0');
      function tick() {
        let d = Math.max(0, end - Date.now());
        const days = Math.floor(d / 86400000); d -= days * 86400000;
        const hours = Math.floor(d / 3600000); d -= hours * 3600000;
        const mins = Math.floor(d / 60000); d -= mins * 60000;
        const secs = Math.floor(d / 1000);
        const set = (sel, v) => { const el = $(sel, box); if (el) el.textContent = pad(v); };
        set('[data-cd-days]', days); set('[data-cd-hours]', hours);
        set('[data-cd-mins]', mins); set('[data-cd-secs]', secs);
      }
      tick(); setInterval(tick, 1000);
    });
  }

  /* ============================================================
     READ MORE TOGGLE (collection description)
     ============================================================ */
  document.addEventListener('click', e => {
    const toggle = e.target.closest('[data-toggle-more]');
    if (!toggle) return;
    const target = document.getElementById(toggle.dataset.toggleMore);
    if (!target) return;
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    target.hidden = open;
    toggle.textContent = open ? toggle.dataset.moreLabel || 'Read More +' : toggle.dataset.lessLabel || 'Read Less –';
  });

  /* ============================================================
     PREDICTIVE SEARCH — product suggestions in the search modal
     ============================================================ */
  const escHtml = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  let ssTimer = null;
  let ssMoved = false;

  function suggestBoxFor(input) {
    const form = input.closest('form');
    return form && form.parentElement ? form.parentElement.querySelector('[data-search-suggest]') : null;
  }

  function hideSuggest() {
    $$('[data-search-suggest]').forEach(box => { box.hidden = true; box.innerHTML = ''; });
    $$('[data-search-input]').forEach(input => input.setAttribute('aria-expanded', 'false'));
  }

  async function fetchSuggest(q, input) {
    const box = suggestBoxFor(input);
    if (!box || !input) return;
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}` +
        `&resources[type]=product&resources[limit]=6` +
        `&resources[options][unavailable_products]=last` +
        `&resources[options][fields]=title,product_type,variants.title,tag`;
      const res = await fetch(url);
      const data = await res.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      renderSuggest(products, q, box, input);
    } catch { /* keep whatever is shown */ }
  }

  function renderSuggest(products, q, box, input) {
    const money = cents => (window.DC ? window.DC.formatMoney(cents) : (cents / 100));
    const searchUrl = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
    let html;
    if (products.length) {
      html = products.map((p, i) => {
        const price = showPrices
          ? money(Math.round(parseFloat(p.price || 0) * 100))
          : 'Login for price';
        const img = p.image
          ? `<img src="${p.image}${p.image.includes('?') ? '&' : '?'}width=100" alt="" loading="lazy">`
          : '';
        return `<a class="ss-item${i === 0 ? ' is-active' : ''}" href="${p.url}" data-ss-index="${i}">` +
          `<span class="ss-img">${img}</span>` +
          `<span class="ss-title">${escHtml(p.title)}</span>` +
          `<span class="ss-price">${price}</span></a>`;
      }).join('');
      html += `<a class="ss-all" href="${searchUrl}">View all results for &ldquo;${escHtml(q)}&rdquo;</a>`;
    } else {
      html = `<p class="ss-none">No products match &ldquo;${escHtml(q)}&rdquo; — try a dress, skirt or co-ord.</p>` +
        `<a class="ss-all" href="${searchUrl}">Search anyway</a>`;
    }
    ssMoved = false;
    box.innerHTML = html;
    box.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  document.addEventListener('input', e => {
    const input = e.target.closest('[data-search-input]');
    if (!input) return;
    clearTimeout(ssTimer);
    const q = input.value.trim();
    const box = suggestBoxFor(input);
    if (q.length < 2 || !box) { hideSuggest(); return; }
    ssTimer = setTimeout(() => fetchSuggest(q, input), 220);
  });

  /* click away from the bar/modal closes the suggestions */
  document.addEventListener('click', e => {
    if (!e.target.closest('[data-search-suggest], [data-search-input]')) hideSuggest();
  });

  document.addEventListener('keydown', e => {
    const input = e.target.closest && e.target.closest('[data-search-input]');
    if (!input) return;
    const box = suggestBoxFor(input);
    if (!box || box.hidden) return;
    const items = $$('.ss-item', box);
    if (!items.length) return;
    const active = $('.ss-item.is-active', box);
    const idx = items.indexOf(active);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      ssMoved = true;
      const next = e.key === 'ArrowDown'
        ? (idx + 1) % items.length
        : (idx - 1 + items.length) % items.length;
      items.forEach(el => el.classList.toggle('is-active', el === items[next]));
      items[next].scrollIntoView({ block: 'nearest' });
    }
    if (e.key === 'Enter' && ssMoved && active) {
      e.preventDefault();
      window.location.href = active.getAttribute('href');
    }
    if (e.key === 'Escape') hideSuggest();
  });

  /* ============================================================
     PDP — gallery · colour · size · qty · add to bag · sticky bar
     ============================================================ */
  function initPDP() {
    $$('[data-pdp]').forEach(root => {
      if (root.dataset.pdpBound) return;
      root.dataset.pdpBound = '1';

      const dataScript = $('[data-variant-data]', root);
      const variants = dataScript ? JSON.parse(dataScript.textContent) : [];
      const optionCount = root.dataset.optionCount ? parseInt(root.dataset.optionCount, 10) : 0;
      const addBtn = $('[data-pdp-add]', root);
      const priceNow = $('[data-price-now]', root);
      const priceWas = $('[data-price-was]', root);
      const priceOff = $('[data-price-off]', root);
      const stickyPrice = $('[data-sticky-price]', root);

      /* selected options state */
      const selected = [];
      $$('[data-option-row]', root).forEach(row => {
        const idx = parseInt(row.dataset.optionRow, 10);
        const pressed = $('[data-option-value][aria-pressed="true"]', row);
        selected[idx] = pressed ? pressed.dataset.optionValue : null;
      });

      /* products without variations render no option UI — take the only variant */
      if (variants.length === 1) variants[0].options.forEach((opt, i) => { selected[i] = opt; });

      function currentVariant() {
        return variants.find(v => v.options.every((opt, i) => opt === selected[i])) || null;
      }

      function updateAdd(v) {
        if (!addBtn) return;
        const t = addBtn.querySelector('[data-add-label]') || addBtn;
        const label = addBtn.dataset.labelAdd || 'Add to Bag';
        if (!v) {
          /* no full variant chosen yet (e.g. size pending) — keep the base price on the button */
          addBtn.disabled = false;
          const base = parseInt(root.dataset.basePrice, 10) || 0;
          t.textContent = showPrices ? `${label} — ${formatMoney(base)}` : label;
          return;
        }
        if (v.available) {
          addBtn.disabled = false;
          t.textContent = showPrices ? `${label} — ${formatMoney(v.price)}` : label;
        } else {
          addBtn.disabled = true;
          t.textContent = addBtn.dataset.labelSold || 'Sold Out';
        }
      }

      function updatePrice(v) {
        if (!v) return;
        if (!showPrices) return; /* prices hidden for signed-out shoppers */
        if (priceNow) priceNow.textContent = formatMoney(v.price);
        if (priceWas) {
          const was = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : null;
          priceWas.textContent = was ? formatMoney(was) : '';
          priceWas.style.display = was ? '' : 'none';
        }
        if (priceOff) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            const pct = Math.round((1 - v.price / v.compare_at_price) * 100);
            priceOff.textContent = `Save ${pct}%`;
            priceOff.style.display = '';
          } else {
            priceOff.style.display = 'none';
          }
        }
        if (stickyPrice) {
          const was = v.compare_at_price && v.compare_at_price > v.price ? ` <s>${formatMoney(v.compare_at_price)}</s>` : '';
          stickyPrice.innerHTML = `${formatMoney(v.price)}${was}`;
        }
      }

      /* option buttons */
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-option-value]');
        if (!btn || btn.disabled) return;
        const row = btn.closest('[data-option-row]');
        const idx = parseInt(row.dataset.optionRow, 10);
        $$('[data-option-value]', row).forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
        selected[idx] = btn.dataset.optionValue;

        /* colour name label */
        if (row.dataset.optionLabel && $(row.dataset.optionLabel, root)) {
          $(row.dataset.optionLabel, root).textContent = btn.dataset.optionValue;
        }

        syncAvailability();
        const v = currentVariant();
        updatePrice(v);
        updateAdd(v);
      });

      /* disable sizes that don't exist / aren't available for the current colour */
      function syncAvailability() {
        $$('[data-option-row]', root).forEach(row => {
          const idx = parseInt(row.dataset.optionRow, 10);
          $$('[data-option-value]', row).forEach(btn => {
            if (row.dataset.optionType !== 'size') { btn.disabled = false; return; }
            const probe = selected.slice();
            probe[idx] = btn.dataset.optionValue;
            const matches = variants.filter(v => probe.every((opt, i) => opt === null || v.options[i] === opt));
            btn.disabled = !matches.length || !matches.some(v => v.available);
          });
        });
      }
      syncAvailability();

      const v0 = currentVariant();
      updatePrice(v0);
      updateAdd(v0);

      /* quantity */
      let qty = 1;
      const qtyVal = $('[data-qty-val]', root);
      root.addEventListener('click', e => {
        if (e.target.closest('[data-qty-minus]')) { qty = Math.max(1, qty - 1); if (qtyVal) qtyVal.textContent = qty; }
        if (e.target.closest('[data-qty-plus]')) { qty = Math.min(9, qty + 1); if (qtyVal) qtyVal.textContent = qty; }
      });

      /* add to bag */
      const addToBag = async () => {
        const sizeRow = $('[data-option-type="size"]', root);
        if (sizeRow && !selected[parseInt(sizeRow.dataset.optionRow, 10)]) {
          showToast('Please choose a size first');
          return;
        }
        const v = currentVariant();
        if (!v) { showToast('Please choose your options first'); return; }
        if (!v.available) { showToast('Sorry — this item is sold out'); return; }
        const ok = await addToCart(v.id, qty, `Added to your bag — ${v.title.replace(/ \/ /g, ' ')} × ${qty}`);
        if (ok) { qty = 1; if (qtyVal) qtyVal.textContent = qty; }
      };
      if (addBtn) addBtn.addEventListener('click', addToBag);
      const stickyAdd = $('[data-sticky-add]', root);
      if (stickyAdd) stickyAdd.addEventListener('click', addToBag);

      /* sticky buy bar — slides in once the buy row leaves the viewport */
      const stickyBuy = $('[data-sticky-buy]', root);
      const buyRow = $('.pdp__buy', root);
      if (stickyBuy && buyRow && 'IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          stickyBuy.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0);
        }, { threshold: 0 }).observe(buyRow);
      }

      /* gallery */
      const mainImg = $('[data-main-img]', root);
      const thumbs = $$('[data-thumb]', root);
      let thumbIdx = Math.max(0, thumbs.findIndex(t => t.classList.contains('is-active')));
      function selectThumb(i, instant) {
        if (!thumbs.length) return;
        thumbIdx = (i + thumbs.length) % thumbs.length;
        const t = thumbs[thumbIdx];
        thumbs.forEach(x => x.classList.toggle('is-active', x === t));
        if (!mainImg) return;
        if (instant) { mainImg.src = t.dataset.full; return; }
        mainImg.style.opacity = '0';
        setTimeout(() => { mainImg.src = t.dataset.full; mainImg.style.opacity = '1'; }, 200);
      }
      thumbs.forEach((t, i) => t.addEventListener('click', () => selectThumb(i)));

      /* swipe through the gallery on touch */
      let galX = null;
      const galEl = $('.pdp__main', root);
      if (galEl) {
        galEl.addEventListener('touchstart', e => { galX = e.touches[0].clientX; }, { passive: true });
        galEl.addEventListener('touchend', e => {
          if (galX === null) return;
          const dx = e.changedTouches[0].clientX - galX;
          if (Math.abs(dx) > 50) selectThumb(thumbIdx + (dx < 0 ? 1 : -1));
          galX = null;
        }, { passive: true });
      }
    });
  }

  /* ============================================================
     COLLECTION — filters · sort · chips · load more
     ============================================================ */
  function initCollection() {
    const root = $('[data-collection]');
    if (!root || root.dataset.collBound) return;
    root.dataset.collBound = '1';

    const form = $('[data-filter-form]', root);
    const grid = $('.grid-products', root);
    const baseUrl = root.dataset.collectionUrl;
    const sectionId = root.dataset.sectionId;

    function serialize() {
      const params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((value, key) => {
          if (value !== '' && value != null) params.append(key, value);
        });
      }
      /* price band → native price range params */
      const band = $('[data-band]:checked', root);
      if (band) {
        if (band.dataset.gte) params.set('filter.v.price.gte', band.dataset.gte);
        if (band.dataset.lte) params.set('filter.v.price.lte', band.dataset.lte);
      }
      return params;
    }

    async function applyFilters(url) {
      const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}`;
      root.classList.add('is-loading');
      try {
        const res = await fetch(fetchUrl);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-collection]');
        if (!fresh) throw new Error('bad response');
        root.innerHTML = fresh.innerHTML;
        root.dataset.collBound = '';
        history.pushState({}, '', url);
        initCollection();
      } catch {
        window.location.href = url;
      }
    }

    /* filter inputs + sort */
    if (form) {
      form.addEventListener('change', e => {
        /* price bands behave as a single choice */
        if (e.target.matches('[data-band]')) {
          $$('[data-band]', form).forEach(b => { if (b !== e.target) b.checked = false; });
        }
        applyFilters(`${baseUrl}?${serialize().toString()}`);
      });
    }

    /* sort select (outside the form in the toolbar) */
    const sortSel = $('[data-sort]', root);
    if (sortSel) {
      sortSel.addEventListener('change', () => {
        const params = serialize();
        params.set('sort_by', sortSel.value);
        applyFilters(`${baseUrl}?${params.toString()}`);
      });
    }

    /* chips / clear-all are plain links — intercept for smooth swap */
    root.addEventListener('click', e => {
      const chipLink = e.target.closest('a[data-chip-remove], a[data-clear-all]');
      if (chipLink) {
        e.preventDefault();
        applyFilters(chipLink.getAttribute('href'));
      }
    });

    /* load more — Section Rendering append */
    root.addEventListener('click', async e => {
      const btn = e.target.closest('[data-load-more]');
      if (!btn) return;
      btn.disabled = true;
      const nextUrl = btn.getAttribute('href') || btn.dataset.nextUrl;
      try {
        const res = await fetch(`${nextUrl}${nextUrl.includes('?') ? '&' : '?'}section_id=${sectionId}`);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-collection]');
        if (fresh) {
          const freshGrid = fresh.querySelector('.grid-products');
          const emptyState = $('.empty', root);
          if (emptyState) emptyState.hidden = true;
          if (freshGrid && grid) {
            $$('.card', freshGrid).forEach(card => grid.appendChild(card));
          }
          const newBtn = fresh.querySelector('[data-load-more]');
          const loadWrap = $('.loadmore', root);
          if (newBtn && loadWrap) {
            btn.setAttribute('href', newBtn.getAttribute('href') || newBtn.dataset.nextUrl);
            btn.textContent = newBtn.textContent;
            btn.disabled = false;
          } else if (loadWrap) {
            loadWrap.style.display = 'none';
          }
          const freshMeta = fresh.querySelector('[data-grid-meta]');
          const meta = $('[data-grid-meta]', root);
          if (freshMeta && meta) meta.textContent = freshMeta.textContent;
        }
      } catch {
        window.location.href = nextUrl;
      }
    });

    /* mobile filters drawer */
    root.addEventListener('click', e => {
      if (e.target.closest('[data-filters-open]')) {
        const filters = $('#filters', root);
        if (filters) {
          filters.classList.add('is-open');
          const overlay = $('#overlay');
          if (overlay) overlay.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        }
      }
      if (e.target.closest('[data-filters-close]')) closeOverlays();
      if (e.target.closest('[data-filters-apply]')) closeOverlays();
    });
  }

  /* ============================================================
     RELATED PRODUCTS — fetch recommendations section
     ============================================================ */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(el => {
      if (el.dataset.recBound) return;
      el.dataset.recBound = '1';
      const url = el.dataset.url;
      if (!url) return;
      fetch(url)
        .then(res => res.text())
        .then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim() !== '') {
            el.innerHTML = fresh.innerHTML;
            initReveal(el);
          }
        })
        .catch(() => {});
    });
  }

  /* ============================================================
     BOOT — init everything, re-init on customizer re-renders
     ============================================================ */
  function initAll(root = document) {
    initAnnounce();
    initScrollFX();
    initReveal(root);
    initFooterAccs();
    initHero();
    initTabs();
    initCountdowns();
    initPDP();
    initCollection();
    initCollectionDots(root);
    initRecommendations();
    updateWishUI(getWish());
  }

  /* USP + reviews dots (rails rendered server-side) */
  function initCollectionDots(root = document) {
    $$('[data-usp-rail]', root).forEach(rail => {
      bindDots(rail, $(`[data-usp-dots="${rail.dataset.uspRail}"]`, root) || $('.usp__dots', root), 'li');
    });
    $$('[data-reviews-rail]', root).forEach(rail => {
      bindDots(rail, $(`[data-reviews-dots="${rail.dataset.reviewsRail}"]`, root) || $('.reviews-wrap .rail-dots', root), '.review');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* customizer: section re-rendered */
  document.addEventListener('shopify:section:load', e => initAll(e.target));

  /* expose helpers for sections */
  window.DC = { showToast, bindDots, formatMoney, addToCart, updateCartCount };
})();
